import React, { useState } from 'react';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, KICKER_FONT_SIZE_LEVELS, DEFAULT_KICKER_FONT_SIZE, SUBTITLE_FONT_SIZE_LEVELS, DEFAULT_SUBTITLE_FONT_SIZE, STATS_FONT_SIZE_LEVELS, DEFAULT_STATS_FONT_SIZE, TAGS_FONT_SIZE_LEVELS, DEFAULT_TAGS_FONT_SIZE } from '../constants/typography';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';
import { InlineEditable } from '../components/canvas/InlineEditable';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';

/**
 * Normies Before/After layout — Canvas Story template.
 *
 * Shows original and current images side by side with canvas diff stats.
 * Images are pixelated. No blur, no glass, no rounded corners.
 *
 * Bold sizing — fills the 1080×1350 canvas confidently.
 */
export function NormiesBeforeAfterLayout({ data, fontPreset, pc }) {
  const { interactive, onElementClick, editingField, onEditChange, onEndEdit } = usePreviewInteraction();
  const [origLoaded, setOrigLoaded] = useState(false);
  const [currLoaded, setCurrLoaded] = useState(false);

  const titleSize = TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74;
  const kickerSize = KICKER_FONT_SIZE_LEVELS[data.kickerFontSize ?? DEFAULT_KICKER_FONT_SIZE]?.size || 16;
  const subtitleSize = SUBTITLE_FONT_SIZE_LEVELS[data.subtitleFontSize ?? DEFAULT_SUBTITLE_FONT_SIZE]?.size || 20;
  const tagsSize = TAGS_FONT_SIZE_LEVELS[data.tagsFontSize ?? DEFAULT_TAGS_FONT_SIZE]?.size || 11;
  const statsSize = STATS_FONT_SIZE_LEVELS[data.statsFontSize ?? DEFAULT_STATS_FONT_SIZE]?.size || 20;

  const originalImage = data.normies?.heroImageOriginal;
  const currentImage = data.normies?.heroImage;
  const diff = data.normies?.apiSnapshot?.canvasDiff;

  const added = diff?.addedCount ?? 0;
  const removed = diff?.removedCount ?? 0;
  const net = diff?.netChange ?? 0;

  const handleTitleClick = (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === 'title') return;
    e.stopPropagation();
    onElementClick('header', 'title');
  };

  const imgStyle = {
    imageRendering: 'pixelated',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  };

  return (
    <>
      {/* Dark overlay for contrast */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.12) 25%, rgba(0,0,0,0.12) 65%, rgba(0,0,0,0.65) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Kicker — top */}
      <NudgeableGroup groupId="content" className="absolute top-[50px] left-[60px] right-[60px]">
        {data.kickerEnabled !== false && data.kicker && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(kickerSize, 18)}px`,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: pc.el.kicker || pc.textMuted,
              textTransform: 'uppercase',
              marginBottom: '12px',
            }}
          >
            {data.kicker}
          </div>
        )}

        {/* Title — bold, fills width */}
        <InlineEditable
          value={data.title}
          isEditing={editingField === 'title'}
          onChange={(html) => onEditChange && onEditChange('title', html)}
          onBlur={onEndEdit}
          className={`leading-[0.95] w-full${editingField === 'title' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
          style={{
            fontSize: `${Math.max(titleSize, 64)}px`,
            fontFamily: fontPreset.title,
            fontWeight: fontPreset.titleWeight || 800,
            color: pc.el.title || pc.textPrimary,
            textTransform: 'uppercase',
            letterSpacing: '-0.03em',
          }}
          onClick={handleTitleClick}
        >
          {data.title}
        </InlineEditable>
      </NudgeableGroup>

      {/* Before / After images — Polaroid frames */}
      <div className="absolute" style={{ top: '260px', left: '48px', right: '48px', height: '660px' }}>
        <div style={{ display: 'flex', gap: '24px', height: '100%' }}>
          {/* Original — Polaroid */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f5f2ed',
            boxShadow: '0 6px 28px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.18)',
            padding: '20px 20px 0 20px',
          }}>
            <div style={{
              flex: 1,
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#e3e5e4',
            }}>
              {originalImage ? (
                <img
                  src={originalImage}
                  alt="Original"
                  style={{ ...imgStyle, opacity: origLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
                  onLoad={() => setOrigLoaded(true)}
                  crossOrigin="anonymous"
                />
              ) : (
                <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '13px', color: 'rgba(0,0,0,0.25)' }}>No original</span>
              )}
            </div>
            <div style={{
              height: '72px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: '18px',
              fontWeight: 700,
              letterSpacing: '0.22em',
              color: 'rgba(0,0,0,0.40)',
              textTransform: 'uppercase',
            }}>
              ORIGINAL
            </div>
          </div>

          {/* Current — Polaroid */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f5f2ed',
            boxShadow: '0 6px 28px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.18)',
            padding: '20px 20px 0 20px',
          }}>
            <div style={{
              flex: 1,
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#e3e5e4',
            }}>
              {currentImage ? (
                <img
                  src={currentImage}
                  alt="Current"
                  style={{ ...imgStyle, opacity: currLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
                  onLoad={() => setCurrLoaded(true)}
                  crossOrigin="anonymous"
                />
              ) : (
                <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '13px', color: 'rgba(0,0,0,0.25)' }}>No image</span>
              )}
            </div>
            <div style={{
              height: '72px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: '18px',
              fontWeight: 700,
              letterSpacing: '0.22em',
              color: 'rgba(0,0,0,0.40)',
              textTransform: 'uppercase',
            }}>
              CURRENT
            </div>
          </div>
        </div>
      </div>

      {/* Canvas diff stats bar — bold readout */}
      <div className="absolute" style={{ bottom: '210px', left: '48px', right: '48px' }}>
        <div style={{
          display: 'flex',
          gap: '32px',
          justifyContent: 'center',
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: `${Math.max(statsSize, 14)}px`,
          fontWeight: 700,
          letterSpacing: '0.06em',
        }}>
          <span style={{ color: '#34d399' }}>+{added} ADDED</span>
          <span style={{ color: pc.textSubtle, opacity: 0.4 }}>/</span>
          <span style={{ color: '#f87171' }}>−{removed} REMOVED</span>
          <span style={{ color: pc.textSubtle, opacity: 0.4 }}>/</span>
          <span style={{ color: net >= 0 ? '#34d399' : '#f87171' }}>NET {net >= 0 ? '+' : ''}{net}</span>
        </div>
      </div>

      {/* Subtitle + Tags — bottom */}
      <div className="absolute bottom-[90px] left-[60px] right-[60px]">
        {data.subtitleEnabled !== false && data.subtitle && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(subtitleSize, 18)}px`,
              color: pc.el.subtitle || pc.textMuted,
              textAlign: 'center',
              marginBottom: '16px',
              letterSpacing: '0.04em',
            }}
          >
            {data.subtitle}
          </div>
        )}

        {data.tagsEnabled !== false && data.meta && data.meta.length > 0 && (
          <div className="flex flex-wrap gap-3 justify-center">
            {data.meta.map((tag, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: `${Math.max(tagsSize, 12)}px`,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: pc.el.tagText || pc.textMuted,
                  border: `1px solid ${pc.el.tagBorder || pc.accent(0.30)}`,
                  padding: '5px 14px',
                  textTransform: 'uppercase',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-[36px] left-[60px] right-[60px]">
        <SlideFooter data={data} pc={pc} />
      </div>
    </>
  );
}
